import { CartAddEvent } from '@theme/events';

/**
 * Adds the configured gift exactly once when the second cart threshold is reached.
 * The markup is present for server-side progress calculations, but this component
 * only acts for visitors placed in the experiment by the body class.
 */
class CartMysteryGift extends HTMLElement {
  #bodyClassObserver;
  #isAdding = false;
  #lastAttemptedVariantId;

  static observedAttributes = ['data-qualified', 'data-present', 'data-variant-id'];

  connectedCallback() {
    this.addGiftWhenEligible();
    this.#bodyClassObserver = new MutationObserver(() => this.addGiftWhenEligible());
    this.#bodyClassObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  attributeChangedCallback(name, previousValue, currentValue) {
    if (name === 'data-qualified' && currentValue !== 'true') {
      this.#lastAttemptedVariantId = undefined;
    }

    if (name && previousValue !== currentValue && this.isConnected) {
      this.addGiftWhenEligible();
    }
  }

  disconnectedCallback() {
    this.#bodyClassObserver?.disconnect();
  }

  async addGiftWhenEligible() {
    const { qualified, present, testClass, variantId } = this.dataset;

    if (
      qualified !== 'true' ||
      present === 'true' ||
      !variantId ||
      !testClass ||
      !document.body.classList.contains(testClass) ||
      document.body.dataset.mysteryGiftLoading === variantId ||
      this.#isAdding ||
      this.#lastAttemptedVariantId === variantId
    ) {
      return;
    }

    this.#isAdding = true;
    this.#lastAttemptedVariantId = variantId;
    this.dataset.loading = 'true';
    this.setAttribute('aria-busy', 'true');
    document.body.dataset.mysteryGiftLoading = variantId;
    const loadingStartedAt = performance.now();

    try {
      await new Promise((resolve) => window.requestAnimationFrame(resolve));

      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                _mc_cart_second_threshold_mystery_gift: 'true',
              },
            },
          ],
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Unable to add mystery gift');
      }

      const cartResponse = await fetch(Theme.routes.cart_url, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      const giftIsPresent = cart.items?.some((item) => String(item.variant_id ?? item.id) === String(variantId));

      if (!giftIsPresent) {
        throw new Error('Mystery gift was not returned in the cart');
      }

      const remainingLoadingTime = 350 - (performance.now() - loadingStartedAt);

      if (remainingLoadingTime > 0) {
        await new Promise((resolve) => window.setTimeout(resolve, remainingLoadingTime));
      }

      this.dataset.present = 'true';

      document.dispatchEvent(
        new CartAddEvent(cart, `mc-cart-mystery-gift-${variantId}`, {
          source: 'mc-cart-mystery-gift',
          itemCount: 1,
          variantId,
        })
      );
    } catch (error) {
      console.error('Mystery gift could not be added to cart.', error);
    } finally {
      this.#isAdding = false;
      delete this.dataset.loading;
      this.removeAttribute('aria-busy');

      if (document.body.dataset.mysteryGiftLoading === variantId) {
        delete document.body.dataset.mysteryGiftLoading;
      }
    }
  }
}

if (!customElements.get('cart-mystery-gift')) {
  customElements.define('cart-mystery-gift', CartMysteryGift);
}
